/**
 * lookupsApi — RTK Query slice cho mọi endpoint /lookups/* (FE dropdowns).
 * Cache 5 phút (keepUnusedDataFor: 300). Tag chung 'Lookup' để invalidate khi cần.
 */
import { baseApi } from './baseApi'
import type {
  LookupCertification,
  LookupFuturesMonth,
  LookupLicense,
  LookupMeOrIb,
  LookupOption,
  LookupQuality,
  LookupTrader,
  LookupVendor,
} from '@/types/api'

const FIVE_MIN = 300

// BE trả `{ items: [...] }`, FE consume array trực tiếp.
const unwrapItems = <T>(resp: { items?: T[] } | T[] | null | undefined): T[] => {
  if (Array.isArray(resp)) return resp
  if (resp && Array.isArray((resp as { items?: T[] }).items)) return (resp as { items: T[] }).items
  return []
}

export const lookupsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listContractTypes: b.query<LookupOption[], void>({
      query: () => '/lookups/contract-types/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'contract-types' }],
    }),
    listMeOrIb: b.query<LookupMeOrIb[], void>({
      query: () => '/lookups/me-or-ib/list',
      transformResponse: unwrapItems<LookupMeOrIb>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'me-or-ib' }],
    }),
    listPaymentTypes: b.query<LookupOption[], void>({
      query: () => '/lookups/payment-types/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'payment-types' }],
    }),
    listForwardTypes: b.query<LookupOption[], void>({
      query: () => '/lookups/forward-types/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'forward-types' }],
    }),
    listFixationCalls: b.query<LookupOption[], void>({
      query: () => '/lookups/fixation-calls/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'fixation-calls' }],
    }),
    listPriceUnits: b.query<LookupOption[], void>({
      query: () => '/lookups/price-units/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'price-units' }],
    }),
    listFuturesMonths: b.query<LookupFuturesMonth[], void>({
      query: () => '/lookups/futures-months/list',
      transformResponse: unwrapItems<LookupFuturesMonth>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'futures-months' }],
    }),
    listQualities: b.query<LookupQuality[], { local_only?: boolean } | void>({
      query: (params) => ({
        url: '/lookups/qualities/list',
        params: params ?? {},
      }),
      transformResponse: unwrapItems<LookupQuality>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'qualities' }],
    }),
    listCertifications: b.query<LookupCertification[], void>({
      query: () => '/lookups/certifications/list',
      transformResponse: unwrapItems<LookupCertification>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'certifications' }],
    }),
    listDeliveryPlaces: b.query<LookupOption[], void>({
      query: () => '/lookups/delivery-places/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'delivery-places' }],
    }),
    listDeliveryTerms: b.query<LookupOption[], void>({
      query: () => '/lookups/delivery-terms/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'delivery-terms' }],
    }),
    listPackages: b.query<LookupOption[], void>({
      query: () => '/lookups/packages/list',
      transformResponse: unwrapItems<LookupOption>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'packages' }],
    }),
    /** licenses theo cp_code (vendor). Khi cp_code rỗng -> trả [] hoặc all (BE quyết). */
    listLicenses: b.query<LookupLicense[], { cp_code?: string } | void>({
      query: (params) => ({
        url: '/lookups/licenses/list',
        params: params ?? {},
      }),
      transformResponse: unwrapItems<LookupLicense>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: (_r, _e, arg) => [
        { type: 'Lookup', id: `licenses-${(arg && arg.cp_code) || 'all'}` },
      ],
    }),
    listTraders: b.query<LookupTrader[], void>({
      query: () => '/lookups/traders/list',
      transformResponse: unwrapItems<LookupTrader>,
      keepUnusedDataFor: FIVE_MIN,
      providesTags: [{ type: 'Lookup', id: 'traders' }],
    }),
    /** Vendor lookup search (autocomplete). Cache theo q. */
    lookupVendors: b.query<LookupVendor[], { q?: string } | void>({
      query: (params) => ({
        url: '/lookups/vendors/lookup',
        params: params ?? {},
      }),
      transformResponse: unwrapItems<LookupVendor>,
      keepUnusedDataFor: 60,
      providesTags: (_r, _e, arg) => [
        { type: 'Lookup', id: `vendors-${(arg && arg.q) || ''}` },
      ],
    }),
  }),
})

export const {
  useListContractTypesQuery,
  useListMeOrIbQuery,
  useListPaymentTypesQuery,
  useListForwardTypesQuery,
  useListFixationCallsQuery,
  useListPriceUnitsQuery,
  useListFuturesMonthsQuery,
  useListQualitiesQuery,
  useListCertificationsQuery,
  useListDeliveryPlacesQuery,
  useListDeliveryTermsQuery,
  useListPackagesQuery,
  useListLicensesQuery,
  useListTradersQuery,
  useLookupVendorsQuery,
} = lookupsApi
